'use client';

/**
 * RoleGuard — étanchéité des interfaces particulier / prestataire (RÈGLE DURABLE).
 *
 * Monté une fois dans app/layout.tsx : surveille chaque navigation.
 *   - Un compte PRESTATAIRE (prestataire_profile existe) ne voit JAMAIS les écrans
 *     particulier → renvoyé vers son onboarding (draft/pending) ou son dashboard (accepted).
 *   - Un compte PARTICULIER (pas de prestataire_profile) ne voit JAMAIS /prestataire/*
 *     → renvoyé vers /particulier.
 *   - Admin : accès à tout.
 *
 * Tout NOUVEL écran doit être rangé dans une des deux zones ci-dessous
 * (ou rester neutre s'il est vraiment commun : /bienvenue, /about, /help…).
 * La protection des données reste côté serveur (requireRole / prestataireProcedure) ;
 * ici on garantit que l'UI de l'autre rôle n'est même pas visible.
 */

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { useCompte } from '@/lib/auth/client';
import { trpc } from '@/lib/trpc/client';

// Zone PARTICULIER — interdite aux prestataires
// `/client` retiré le 01/09 avec la suppression de `app/client/**` (interface
// client de RapidPro, doublon de `/particulier`).
const CLIENT_ZONE = ['/', '/particulier', '/mon-espace', '/onboarding', '/dashboard', '/search'];
// Zone PRESTATAIRE — interdite aux particuliers
const PRESTA_ZONE = ['/prestataire'];

function inZone(pathname: string, zone: string[]): boolean {
  return zone.some((p) => (p === '/' ? pathname === '/' : pathname === p || pathname.startsWith(`${p}/`)));
}

/**
 * La maison du prestataire est `/prestataire`, QUEL QUE SOIT son statut.
 *
 * Jusqu'au 17/08 un profil `accepted` était expédié sur `/prestataire/dashboard`
 * — l'ancien tableau de bord, hors de l'application rapatriée le 13/08 : ni
 * carte, ni feuillet, ni rangée de pôles, aucun retour. Le piège se refermait à
 * la validation par l'admin : le prestataire y atterrissait, et toute tentative
 * de sortie vers la zone client repassait par ici, qui l'y renvoyait. Boucle
 * sans issue, y compris après redémarrage de l'application.
 *
 * `/prestataire` porte les deux états : validé, c'est l'application ; en cours,
 * le wizard s'y ouvre en modale par-dessus la carte.
 */
function prestaHome(): string {
  return '/prestataire';
}

/**
 * Navigation DURE (window.location) et pas router.replace : le changement de zone
 * fait passer d'un écran lourd à un autre (proto particulier ↔ carte prestataire,
 * MapLibre des deux côtés) dans le MÊME arbre React. Cette bascule à chaud plantait
 * en « Rendered more hooks than during the previous render » → écran blanc, boucle
 * sans sortie (incident 27/07). Un rechargement complet démonte tout : plus rien à
 * réconcilier. C'est un chemin de garde, rare, pas une navigation normale — le coût
 * d'un reload y est acceptable.
 */
function leaveZone(to: string) {
  window.location.replace(to);
}

export default function RoleGuard() {
  const pathname = usePathname();
  const { isSignedIn } = useCompte();
  const me = trpc.users.me.useQuery(undefined, {
    enabled: !!isSignedIn,
    retry: false,
    staleTime: 60_000, // pas de refetch à chaque navigation
  });

  useEffect(() => {
    if (!isSignedIn || !me.data) return;
    const { user, prestataireProfile } = me.data;
    if (user.user_type === 'admin') return;

    if (prestataireProfile && inZone(pathname, CLIENT_ZONE)) {
      leaveZone(prestaHome());
    } else if (!prestataireProfile && inZone(pathname, PRESTA_ZONE)) {
      leaveZone('/particulier');
    }
  }, [pathname, isSignedIn, me.data]);

  return null;
}
